import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Trophy, Star, Gift, Share2, Copy, Check, 
  ArrowUp, ArrowDown, Clock, Loader2, Sparkles
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Progress } from './ui/progress';
import { toast } from 'sonner';
import api from '../lib/api';

// Tier colors and icons
const TIER_STYLES = {
  BRONZE: { color: '#CD7F32', bg: 'bg-amber-900/30', border: 'border-amber-700' },
  SILVER: { color: '#C0C0C0', bg: 'bg-gray-500/30', border: 'border-gray-400' },
  GOLD: { color: '#FFD700', bg: 'bg-yellow-500/30', border: 'border-yellow-500' },
  PLATINUM: { color: '#E5E4E2', bg: 'bg-slate-300/30', border: 'border-slate-300' }
};

const LoyaltyCard = ({ profile, config, onRefresh }) => {
  const { t, i18n } = useTranslation();
  const [showReferralInput, setShowReferralInput] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [applying, setApplying] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!profile) return null;

  const tierStyle = TIER_STYLES[profile.tier] || TIER_STYLES.BRONZE;
  const tierConfig = config?.tiers?.[profile.tier] || { multiplier: 1 };
  const nextTier = profile.tier === 'BRONZE' ? 'SILVER' : 
                   profile.tier === 'SILVER' ? 'GOLD' : 
                   profile.tier === 'GOLD' ? 'PLATINUM' : null;
  const nextTierPoints = nextTier ? config?.tiers?.[nextTier]?.min_points : null;
  const progressToNext = nextTierPoints ? 
    Math.min(100, (profile.lifetime_earnings / nextTierPoints) * 100) : 100;

  const copyReferralCode = () => {
    navigator.clipboard.writeText(profile.referral_code);
    setCopied(true);
    toast.success(i18n.language === 'es' ? 'Código copiado' : 'Code copied');
    setTimeout(() => setCopied(false), 2000);
  };

  const applyReferralCode = async () => {
    if (!referralCode.trim()) return;
    setApplying(true);
    try {
      await api.post('/loyalty/referral/apply', { referral_code: referralCode.trim() });
      toast.success(i18n.language === 'es' ? 
        '¡Código aplicado! 10% de descuento en tu primera reserva' : 
        'Code applied! 10% off your first booking');
      setShowReferralInput(false);
      onRefresh?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al aplicar código');
    } finally {
      setApplying(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`rounded-2xl p-6 ${tierStyle.bg} border ${tierStyle.border}`}
    >
      {/* Header with Tier */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div 
            className="w-12 h-12 rounded-full flex items-center justify-center"
            style={{ backgroundColor: tierStyle.color + '30' }}
          >
            <Trophy className="w-6 h-6" style={{ color: tierStyle.color }} />
          </div>
          <div>
            <p className="text-sm text-zinc-400">
              {i18n.language === 'es' ? 'Tu nivel' : 'Your tier'}
            </p>
            <p className="text-xl font-bold" style={{ color: tierStyle.color }}>
              {profile.tier}
            </p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-sm text-zinc-400">Multiplicador</p>
          <p className="text-lg font-bold text-primary">{tierConfig.multiplier}x</p>
        </div>
      </div>

      {/* Points Display */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-black/30 rounded-xl p-4 text-center">
          <Sparkles className="w-5 h-5 text-primary mx-auto mb-2" />
          <p className="text-3xl font-bold text-white">{profile.available_points}</p>
          <p className="text-sm text-zinc-400">
            {i18n.language === 'es' ? 'Puntos disponibles' : 'Available points'}
          </p>
        </div>
        <div className="bg-black/30 rounded-xl p-4 text-center">
          <Star className="w-5 h-5 text-yellow-500 mx-auto mb-2" />
          <p className="text-3xl font-bold text-white">{profile.lifetime_earnings}</p>
          <p className="text-sm text-zinc-400">
            {i18n.language === 'es' ? 'Total acumulado' : 'Lifetime points'}
          </p>
        </div>
      </div>

      {/* Progress to Next Tier */}
      {nextTier && (
        <div className="mb-6">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-zinc-400">
              {i18n.language === 'es' ? 'Progreso a' : 'Progress to'} {nextTier}
            </span>
            <span className="text-white">{profile.lifetime_earnings} / {nextTierPoints}</span>
          </div>
          <Progress value={progressToNext} className="h-2" />
        </div>
      )}

      {/* Referral Section */}
      <div className="border-t border-white/10 pt-4 mt-4">
        <p className="text-sm text-zinc-400 mb-3 flex items-center gap-2">
          <Gift className="w-4 h-4" />
          {i18n.language === 'es' ? 'Invita amigos y gana puntos' : 'Invite friends and earn points'}
        </p>
        
        <div className="flex gap-2">
          <div className="flex-1 bg-black/40 rounded-lg px-4 py-3 font-mono text-lg text-primary">
            {profile.referral_code}
          </div>
          <Button 
            onClick={copyReferralCode}
            className="btn-primary px-4"
            data-testid="copy-referral-btn"
          >
            {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
          </Button>
        </div>

        <p className="text-xs text-zinc-500 mt-2">
          {i18n.language === 'es' 
            ? `Ganas ${config?.referral_bonus || 500} puntos por cada amigo que reserve`
            : `Earn ${config?.referral_bonus || 500} points for each friend who books`}
        </p>

        {/* Apply someone else's referral code */}
        {!profile.referred_by && (
          <div className="mt-4">
            {!showReferralInput ? (
              <button
                onClick={() => setShowReferralInput(true)}
                className="text-sm text-primary hover:underline"
              >
                {i18n.language === 'es' ? '¿Tienes un código de referido?' : 'Have a referral code?'}
              </button>
            ) : (
              <div className="flex gap-2">
                <Input
                  value={referralCode}
                  onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                  placeholder="PLY-XXXXXXXX"
                  className="bg-black/40 border-white/20 font-mono"
                  data-testid="referral-input"
                />
                <Button
                  onClick={applyReferralCode}
                  disabled={applying || !referralCode.trim()}
                  className="btn-primary"
                  data-testid="apply-referral-btn"
                >
                  {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Aplicar'}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
};

const PointsHistory = ({ history }) => {
  const { i18n } = useTranslation();
  
  if (!history || history.length === 0) {
    return (
      <div className="text-center py-8 text-zinc-500">
        {i18n.language === 'es' ? 'Sin movimientos aún' : 'No transactions yet'}
      </div>
    );
  }

  const getIcon = (type) => {
    switch (type) {
      case 'BOOKING': return <Star className="w-4 h-4 text-yellow-500" />;
      case 'REFERRAL': return <Gift className="w-4 h-4 text-green-500" />;
      case 'REDEMPTION': return <ArrowDown className="w-4 h-4 text-red-500" />;
      case 'WELCOME': return <Sparkles className="w-4 h-4 text-primary" />;
      default: return <Clock className="w-4 h-4 text-zinc-400" />;
    }
  };

  return (
    <div className="space-y-3">
      {history.map((tx) => (
        <motion.div
          key={tx.id}
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          className="flex items-center justify-between p-3 bg-white/5 rounded-lg"
        >
          <div className="flex items-center gap-3">
            {getIcon(tx.transaction_type)}
            <div>
              <p className="text-white text-sm">{tx.description}</p>
              <p className="text-xs text-zinc-500">
                {new Date(tx.created_at).toLocaleDateString(i18n.language === 'es' ? 'es-ES' : 'en-US')}
              </p>
            </div>
          </div>
          <span className={`font-bold ${tx.points > 0 ? 'text-green-500' : 'text-red-500'}`}>
            {tx.points > 0 ? '+' : ''}{tx.points}
          </span>
        </motion.div>
      ))}
    </div>
  );
};

const RedeemPoints = ({ profile, config, onSuccess }) => {
  const { i18n } = useTranslation();
  const [pointsToRedeem, setPointsToRedeem] = useState('');
  const [redeeming, setRedeeming] = useState(false);

  const minRedemption = config?.min_redemption || 500;
  const pointsValue = pointsToRedeem ? (parseInt(pointsToRedeem) / (config?.points_to_euro_ratio || 100)) : 0;
  const availablePoints = profile?.available_points || 0;

  const handleRedeem = async () => {
    const points = parseInt(pointsToRedeem);
    if (isNaN(points) || points < minRedemption) {
      toast.error(i18n.language === 'es' ? `Mínimo ${minRedemption} puntos` : `Minimum ${minRedemption} points`);
      return;
    }
    if (points > availablePoints) {
      toast.error(i18n.language === 'es' ? 'No tienes suficientes puntos' : 'Not enough points');
      return;
    }

    setRedeeming(true);
    try {
      const response = await api.post('/loyalty/redeem', { points });
      toast.success(response.data.message);
      onSuccess?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    } finally {
      setRedeeming(false);
    }
  };

  return (
    <div className="bg-white/5 rounded-xl p-4">
      <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
        <Gift className="w-5 h-5 text-primary" />
        {i18n.language === 'es' ? 'Canjear puntos' : 'Redeem points'}
      </h4>
      
      <div className="flex gap-2 mb-3">
        <Input
          type="number"
          value={pointsToRedeem}
          onChange={(e) => setPointsToRedeem(e.target.value)}
          placeholder={`Min. ${minRedemption}`}
          className="bg-black/40 border-white/20"
          min={minRedemption}
          max={availablePoints}
          data-testid="redeem-points-input"
        />
        <Button
          onClick={handleRedeem}
          disabled={redeeming || !pointsToRedeem}
          className="btn-primary"
          data-testid="redeem-points-btn"
        >
          {redeeming ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Canjear'}
        </Button>
      </div>

      {pointsToRedeem && parseInt(pointsToRedeem) >= minRedemption && (
        <p className="text-sm text-green-500">
          = {pointsValue.toFixed(2)}€ {i18n.language === 'es' ? 'de descuento' : 'discount'}
        </p>
      )}

      <p className="text-xs text-zinc-500 mt-2">
        {i18n.language === 'es' 
          ? `${config?.points_to_euro_ratio || 100} puntos = 1€ de descuento`
          : `${config?.points_to_euro_ratio || 100} points = 1€ discount`}
      </p>
    </div>
  );
};

const LoyaltyWidget = () => {
  const { i18n } = useTranslation();
  const [profile, setProfile] = useState(null);
  const [config, setConfig] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchData = async () => {
    try {
      const [profileRes, historyRes, configRes] = await Promise.all([
        api.get('/loyalty/profile'),
        api.get('/loyalty/history?limit=10'),
        api.get('/loyalty/tiers')
      ]);
      setProfile(profileRes.data);
      setHistory(historyRes.data);
      setConfig(configRes.data);
    } catch (error) {
      console.error('Error fetching loyalty data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="loyalty-widget">
      {/* Tabs */}
      <div className="flex gap-2 bg-white/5 rounded-lg p-1">
        <button
          onClick={() => setActiveTab('overview')}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'overview' ? 'bg-primary text-black' : 'text-zinc-400 hover:text-white'
          }`}
        >
          {i18n.language === 'es' ? 'Resumen' : 'Overview'}
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'history' ? 'bg-primary text-black' : 'text-zinc-400 hover:text-white'
          }`}
        >
          {i18n.language === 'es' ? 'Historial' : 'History'}
        </button>
        <button
          onClick={() => setActiveTab('redeem')}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'redeem' ? 'bg-primary text-black' : 'text-zinc-400 hover:text-white'
          }`}
        >
          {i18n.language === 'es' ? 'Canjear' : 'Redeem'}
        </button>
      </div>

      {/* Content */}
      <AnimatePresence mode="wait">
        {activeTab === 'overview' && (
          <motion.div
            key="overview"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <LoyaltyCard profile={profile} config={config} onRefresh={fetchData} />
          </motion.div>
        )}
        {activeTab === 'history' && (
          <motion.div
            key="history"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <PointsHistory history={history} />
          </motion.div>
        )}
        {activeTab === 'redeem' && (
          <motion.div
            key="redeem"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <RedeemPoints profile={profile} config={config} onSuccess={fetchData} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Info Footer */}
      <div className="bg-white/5 rounded-lg p-4 text-sm text-zinc-400">
        <h5 className="font-medium text-white mb-2">
          {i18n.language === 'es' ? '¿Cómo funciona?' : 'How it works?'}
        </h5>
        <ul className="space-y-1">
          <li>• {i18n.language === 'es' 
            ? `Gana ${config?.points_per_euro || 10} puntos por cada euro gastado`
            : `Earn ${config?.points_per_euro || 10} points per euro spent`}</li>
          <li>• {i18n.language === 'es'
            ? 'Sube de nivel y aumenta tu multiplicador de puntos'
            : 'Level up and increase your points multiplier'}</li>
          <li>• {i18n.language === 'es'
            ? `Invita amigos y gana ${config?.referral_bonus || 500} puntos`
            : `Invite friends and earn ${config?.referral_bonus || 500} points`}</li>
        </ul>
      </div>
    </div>
  );
};

export default LoyaltyWidget;
export { LoyaltyCard, PointsHistory, RedeemPoints };
